import { useState, useMemo, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import IconButton from "@/components/IconButton";
import Button from "@/components/Button";
import Table, {
  TableHeading,
  TableControls,
  TableFooter,
  buildActionColumn,
  buildBulkSelectionColumn,
  buildCopyTextColumn,
  buildEditableCellColumn,
  buildEditableSelectCellColumn
} from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import SearchInput from "@/components/SearchInput";
import Select from "@/components/Select";
import ObjectHeading from "@/components/ObjectHeading";
import usePagination from "@/hooks/usePagination";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import {
  GET_ACTIVITY,
  GET_STAGED_PARTICIPATIONS,
  GET_ALL_STAGED_PARTICIPATION_IDS,
  EXPORT_STAGED_PARTICIPATIONS,
  UPDATE_STAGED_PARTICIPATIONS
} from "../../../../queries";
import { showSnackbar } from "@/lib/snackbarUtils";
import AddParticipantsModal from "./_components/AddParticipantsModal";
import EditParticipationModal from "./_components/EditParticipationModal";
import InlinePersonAutocompleteCell from "./_components/InlinePersonAutocompleteCell";
import RemoveParticipationModal from "./_components/RemoveParticipationModal";
import FinalizeParticipantsModal from "./_components/FinalizeParticipantsModal";
import CancelSessionModal from "./_components/CancelSessionModal";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { actionId } from "@/hooks/useTableRowMenu";
import Icon from "@/components/Icon";
import Tooltip from "@/components/Tooltip";
import type { ColumnDef, CellContext, RowSelectionState } from "@tanstack/react-table";
import { toUTCFormat } from "@/lib/dateHelpers";

import {
  findSelectedCreditRate,
  getParticipationLevelOptions,
  getParticipationQuantityLabel,
  getParticipationRoleOptions,
  shouldShowParticipationQuantity,
  validateParticipationQuantity
} from "./_utils";
import { renderAttendanceType, renderParticipationRole, renderCredits } from "@/pages/activities/activity-types/_utils";
import { ActivityParticipationRole, AttendanceType, CreditRate } from "@/pages/activities/activity-types/types";

const EMPTY_CREDIT_RATES: CreditRate[] = [];

const STAGED_IMPORT_ID_KEY = (activityId: string) => `stagedImportId_${activityId}`;

const VALIDATION_OPTIONS = [
  { value: "all", label: "Show All", displayText: "Show All" },
  { value: "valid", label: "Valid Only", displayText: "Valid Only" },
  { value: "invalid", label: "Invalid Only", displayText: "Invalid Only" }
];

interface AddParticipantRow {
  id: string;
  name: string;
  pgaId: string;
  profilePhoto: string | null;
  attendanceType: AttendanceType;
  participationRole: ActivityParticipationRole;
  creditsToAward: number | string;
  validationErrors: string[];
  email?: string;
  startDate?: string | null;
  endDate?: string | null;
  quantity?: number | null;
}

export default function AddParticipantsPage() {
  const { activityId = "" } = useParams();
  const navigate = useNavigate();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "searchTerm",
    debounceMs: 300
  });
  const { createHeaderPagination, createFooterPagination, resetPagination, paginationVariables } = usePagination();

  const [stagedImportId, setStagedImportIdState] = useState<string | null>(null);

  useEffect(() => {
    if (activityId) {
      try {
        const stored = localStorage.getItem(STAGED_IMPORT_ID_KEY(activityId));
        setStagedImportIdState(stored);
      } catch {
        setStagedImportIdState(null);
      }
    } else {
      setStagedImportIdState(null);
    }
  }, [activityId]);

  const setStagedImportId = (id: string | null) => {
    setStagedImportIdState(id);
    try {
      if (activityId) {
        if (id) {
          localStorage.setItem(STAGED_IMPORT_ID_KEY(activityId), id);
        } else {
          localStorage.removeItem(STAGED_IMPORT_ID_KEY(activityId));
        }
      }
    } catch {
      /* ignore */
    }
  };

  const [validationFilter, setValidationFilter] = useState("all");
  const [addParticipantsModalOpen, setAddParticipantsModalOpen] = useState(false);
  const [editParticipationModalOpen, setEditParticipationModalOpen] = useState(false);
  const [removeParticipationModalOpen, setRemoveParticipationModalOpen] = useState(false);
  const [finalizeParticipantsModalOpen, setFinalizeParticipantsModalOpen] = useState(false);
  const [cancelConfirmModalOpen, setCancelConfirmModalOpen] = useState(false);
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | null>(null);
  const { rowSelection, setRowSelection, bulkSelectionProps, isSelectedOnly, deselectAll, rowSelectionCount } =
    useBulkSelection();

  const [fetchAllIds, { loading: fetchingAllIds }] = useLazyQuery(GET_ALL_STAGED_PARTICIPATION_IDS, {
    fetchPolicy: "network-only"
  });

  const handleRowSelectionChange = useCallback(
    (updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
      setRowSelection(updater);
    },
    [setRowSelection]
  );

  const { data: activityData, loading: activityLoading } = useQuery<any>(GET_ACTIVITY, {
    variables: { id: activityId }
  });

  const stagedFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      hasValidationErrors: validationFilter === "all" ? undefined : validationFilter === "invalid" ? true : false
    }),
    [debouncedSearch, validationFilter]
  );

  const stagedFiltersWithErrors = useMemo(() => ({ hasValidationErrors: true }), []);

  useEffect(() => {
    resetPagination();
  }, [validationFilter, resetPagination]);

  const {
    data: stagedData,
    loading: participantsLoading,
    refetch: refetchParticipants
  } = useQuery<any>(GET_STAGED_PARTICIPATIONS, {
    variables: {
      stagedImportId: stagedImportId ?? "",
      filters: stagedImportId ? stagedFilters : undefined,
      filtersWithErrors: stagedImportId ? stagedFiltersWithErrors : undefined,
      ...paginationVariables
    },
    skip: !stagedImportId,
    onError: (error) => {
      const isNotFound = error.graphQLErrors?.some((e) => e.message?.toLowerCase().includes("not found"));
      if (isNotFound) {
        setStagedImportId(null);
      }
    }
  });

  const [editableCellEpoch, setEditableCellEpoch] = useState(0);
  const resetStagedEditableCells = useCallback(() => {
    setEditableCellEpoch((epoch) => epoch + 1);
    void refetchParticipants();
  }, [refetchParticipants]);

  const [updateStagedParticipations] = useMutation(UPDATE_STAGED_PARTICIPATIONS, {
    onCompleted: async (data) => {
      const result = data?.updateStagedParticipations;
      if (result?.success) {
        await refetchParticipants();
        showSnackbar(result.message ?? "Participation updated", {
          close: true
        });
        return;
      }
      showSnackbar(result?.message ?? "Failed to update participation", { close: true });
      resetStagedEditableCells();
    },
    onError: (error) => {
      showSnackbar(error.message ?? "Failed to update participation", { close: true });
      resetStagedEditableCells();
    }
  });

  const [exportStagedParticipations, { loading: exportLoading }] = useMutation(EXPORT_STAGED_PARTICIPATIONS);

  const handleCsvExport = async () => {
    if (!stagedImportId) {
      showSnackbar("No staged participants to export", { close: true });
      return;
    }
    try {
      const { data } = await exportStagedParticipations({
        variables: { input: { stagedImportId, filters: stagedFilters } }
      });
      const url = data?.exportStagedParticipations?.url;
      if (!url) {
        showSnackbar(data?.exportStagedParticipations?.message ?? "Export failed", { close: true });
        return;
      }
      window.location.assign(url);
    } catch {
      showSnackbar("Unable to export staged participants", { close: true });
    }
  };

  const activity = activityData?.activity;

  const tableData = useMemo<AddParticipantRow[]>(() => {
    const participations = stagedData?.stagedParticipations?.participations?.nodes ?? [];

    return participations.map((p: any) => {
      return {
        id: p.id,
        name: p.name ?? "--",
        pgaId: p.pgaId ?? "",
        profilePhoto: p.profileImage ?? null,
        participationRole: p.participationRole,
        attendanceType: p.attendanceType,
        validationErrors: p.validationErrors ?? [],
        creditsToAward: p.creditsToBeEarned ?? 0,
        email: p.email ?? null,
        startDate: p.startDate ?? null,
        endDate: p.endDate ?? null,
        quantity: p.quantity ?? activity?.duration ?? null
      };
    });
  }, [activity?.duration, stagedData]);

  const errorRowsCount = stagedData?.errorsCount?.totalCount ?? 0;

  const filteredTableData = useMemo(() => {
    if (isSelectedOnly) {
      return tableData.filter((row) => rowSelection[row.id]);
    }
    return tableData;
  }, [tableData, isSelectedOnly, rowSelection]);

  const newParticipantsCount = stagedData?.totalStagedParticipants?.totalCount ?? 0;
  const finalizeable = stagedData?.stagedParticipations?.finalizable ?? false;

  const handleSelectAllAcrossPages = useCallback(async () => {
    if (!stagedImportId) return;
    const { data } = await fetchAllIds({
      variables: { stagedImportId, filters: stagedFilters }
    });
    const nodes = data?.stagedParticipations?.participations?.nodes ?? [];
    const newSelection: RowSelectionState = {};
    for (const node of nodes) {
      newSelection[node.id] = true;
    }
    setRowSelection(newSelection);
  }, [stagedImportId, stagedFilters, fetchAllIds, setRowSelection]);

  const handleCancelClick = () => {
    if (stagedImportId) {
      setCancelConfirmModalOpen(true);
    } else {
      navigate(-1);
    }
  };

  const handleEraseAndLeave = () => {
    setStagedImportId(null);
    navigate(-1);
  };

  const handleFinalizeRegistrations = () => {
    setFinalizeParticipantsModalOpen(true);
  };

  const selectedParticipants = useMemo(() => {
    const ids = Object.keys(rowSelection);
    return ids.map((id) => tableData.find((r) => r.id === id)).filter((row): row is AddParticipantRow => !!row);
  }, [rowSelection, tableData]);

  const creditRates: CreditRate[] = activity?.activityType?.config?.creditRates ?? EMPTY_CREDIT_RATES;
  const quantityLabelRate = creditRates.find((rate) =>
    shouldShowParticipationQuantity(rate.rateUnit, rate.rateMultiplier)
  );
  const showQuantityColumn = !!quantityLabelRate;

  const columns: ColumnDef<AddParticipantRow, unknown>[] = useMemo(
    () => [
      buildBulkSelectionColumn<AddParticipantRow>(),
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }: CellContext<AddParticipantRow, unknown>) => (
          <InlinePersonAutocompleteCell
            name={row.original.name}
            pgaId={row.original.pgaId}
            profilePhoto={row.original.profilePhoto}
            email={row.original.email}
            onEdit={(newPgaId) =>
              updateStagedParticipations({
                variables: {
                  input: {
                    participations: [{ id: row.original.id, pgaId: newPgaId }]
                  }
                }
              })
            }
          />
        ),
        enableSorting: false,
        size: 300,
        meta: { headerClassName: "hide-header-in-column" }
      },
      buildCopyTextColumn<AddParticipantRow>({
        accessorKey: "pgaId",
        header: "PGA ID",
        copyFieldLabel: "PGA ID",
        getText: (row) => row.pgaId || "",
        getPrefix: (row) => {
          const { validationErrors } = row;
          if (!validationErrors?.length) return null;
          const errorMessage =
            (Array.isArray(validationErrors)
              ? validationErrors.map((e) => (typeof e === "string" ? e : ((e as { message?: string })?.message ?? "")))
              : []
            ).join(", ") || "Validation error";
          return (
            <Tooltip variant="plain" color="dark" supportingText={errorMessage} anchorClassName="flex">
              <Icon
                name="error"
                className="text-sys-brand-error flex-shrink-0"
                size={20}
                aria-label={errorMessage || "Validation error"}
              />
            </Tooltip>
          );
        },
        shouldShowCopyButton: (row) => !!row.pgaId,
        enableSorting: false,
        size: 180
      }),
      getParticipationLevelOptions(creditRates).length > 1
        ? buildEditableSelectCellColumn<AddParticipantRow>({
            accessorKey: "attendanceType",
            header: "Attendance Type",
            options: getParticipationLevelOptions(creditRates),
            onEdit: (value, row) => {
              updateStagedParticipations({
                variables: {
                  input: {
                    participations: [{ id: row.id, attendanceType: value }]
                  }
                }
              });
            },
            enableSorting: false,
            size: 200
          })
        : {
            accessorKey: "attendanceType",
            header: "Attendance Type",
            cell: ({ row }: CellContext<AddParticipantRow, unknown>) =>
              renderAttendanceType(row.original.attendanceType),
            enableSorting: false,
            size: 200
          },
      getParticipationRoleOptions(creditRates).length > 1
        ? buildEditableSelectCellColumn<AddParticipantRow>({
            accessorKey: "participationRole",
            header: "Participation Role",
            options: getParticipationRoleOptions(creditRates),
            onEdit: (value, row) => {
              updateStagedParticipations({
                variables: {
                  input: {
                    participations: [{ id: row.id, participationRole: value }]
                  }
                }
              });
            },
            enableSorting: false,
            size: 200
          })
        : {
            accessorKey: "participationRole",
            header: "Participation Role",
            cell: ({ row }: CellContext<AddParticipantRow, unknown>) =>
              renderParticipationRole(row.original.participationRole),
            enableSorting: false,
            size: 200
          },
      {
        accessorKey: "startDate",
        header: "Start Date",
        cell: ({ row }: CellContext<AddParticipantRow, unknown>) => toUTCFormat(row.original.startDate ?? ""),
        enableSorting: false,
        size: 130
      },
      {
        accessorKey: "endDate",
        header: "End Date",
        cell: ({ row }: CellContext<AddParticipantRow, unknown>) => toUTCFormat(row.original.endDate ?? ""),
        enableSorting: false,
        size: 130
      },
      ...(showQuantityColumn
        ? [
            {
              ...buildEditableCellColumn<AddParticipantRow>({
                accessorKey: "quantity",
                header: getParticipationQuantityLabel(quantityLabelRate?.rateUnit),
                editable: (row) => {
                  const selectedRate = findSelectedCreditRate(creditRates, row.attendanceType, row.participationRole);
                  return shouldShowParticipationQuantity(selectedRate?.rateUnit, selectedRate?.rateMultiplier);
                },
                onEdit: (value, row) => {
                  const validation = validateParticipationQuantity(value);
                  if (validation !== true) {
                    showSnackbar(validation, { close: true });
                    resetStagedEditableCells();
                    return;
                  }
                  updateStagedParticipations({
                    variables: {
                      input: {
                        participations: [{ id: row.id, quantity: parseFloat(value) }]
                      }
                    }
                  });
                },
                enableSorting: false,
                size: 150
              }),
              id: `quantity-${editableCellEpoch}`
            }
          ]
        : []),
      {
        accessorKey: "creditsToAward",
        header: "Credits to Award",
        enableSorting: false,
        size: 150
      },
      buildActionColumn<AddParticipantRow>({
        onViewClick: (row) => {
          setSelectedPgaId(row.pgaId || null);
          setIsPersonSideSheetOpen(true);
        },
        showViewButton: (row) => row.validationErrors.length === 0,
        viewButtonTitle: "View participant details",
        actionId,
        setMenu: () => {}
      })
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      updateStagedParticipations,
      activity?.activityType?.config?.creditRates,
      showQuantityColumn,
      quantityLabelRate?.rateUnit,
      resetStagedEditableCells,
      editableCellEpoch,
      stagedData?.stagedParticipations?.participations
    ]
  );

  const stagedResult = stagedData?.stagedParticipations?.participations;

  if (!activity && !activityLoading) {
    return (
      <FocusedPageLayout
        cancelButton={
          <div className="flex cursor-pointer items-center" onClick={handleCancelClick} data-testid="back">
            <IconButton name="arrow_back" iconClassName="text-sys-brand-on-surface" />
            <span className="text-title-large">Cancel</span>
          </div>
        }
      >
        <div className="text-title-large">Activity not found</div>
      </FocusedPageLayout>
    );
  }

  return (
    <FocusedPageLayout
      cancelButton={
        <div className="flex cursor-pointer items-center" onClick={handleCancelClick} data-testid="cancel-button">
          <IconButton name="arrow_back" iconClassName="text-sys-brand-on-surface" />
          <span className="text-title-large">Cancel</span>
        </div>
      }
      actions={
        errorRowsCount === 0 && finalizeable ? (
          <Button
            variant="filled"
            onClick={handleFinalizeRegistrations}
            data-testid="finalize-registrations-button"
            disabled={newParticipantsCount === 0}
          >
            Finalize Participants
          </Button>
        ) : (
          <Tooltip
            variant="plain"
            color="dark"
            supportingText={`${errorRowsCount} rows have errors`}
            anchorClassName="flex"
          >
            <Button
              variant="filled"
              onClick={handleFinalizeRegistrations}
              data-testid="finalize-registrations-button"
              disabled
            >
              Finalize Participants
            </Button>
          </Tooltip>
        )
      }
      stickyActions={true}
    >
      <ObjectHeading
        title={activity?.title ?? ""}
        metaData={[
          {
            text: `${renderCredits(activity?.activityType?.config?.creditRates ?? [], activity?.duration ?? 0)}`,
            label: "PDR Credits"
          },
          {
            text: `${activity?.activityType.title}`,
            label: "Activity Type"
          },
          ...(activity?.allowParticipationStarting
            ? [
                {
                  text: toUTCFormat(activity.allowParticipationStarting) || "",
                  label: "Starts On"
                }
              ]
            : []),
          {
            text: activity?.allowParticipationThrough
              ? toUTCFormat(activity.allowParticipationThrough) || ""
              : "(None)",
            label: "Ends On"
          }
        ]}
        iconName="description"
        isDetailsView
        showFullMetadataInCondensed={true}
      />

      <TableHeading
        heading="Bulk Add Participants"
        body={
          <div>
            <p>
              Use the Add Participants button to stage people to import to this activity. You can edit or remove staged
              participants as needed.
            </p>
            <p className="mt-2 text-body-medium font-bold">
              You must click Finalize Participants to write the participations.
            </p>
            <p className="mt-2 text-body-medium font-bold">New Participants: {newParticipantsCount}</p>
          </div>
        }
        actions={[
          {
            label: "Add Participants",
            icon: "add" as const,
            variant: "filled" as const,
            onClick: () => setAddParticipantsModalOpen(true)
          },
          {
            label: "Export Filtered List",
            icon: "download" as const,
            variant: "text" as const,
            onClick: handleCsvExport,
            disabled: !stagedImportId || exportLoading
          }
        ]}
      />

      <Table<AddParticipantRow>
        data={filteredTableData}
        loading={participantsLoading}
        columns={columns}
        data-testid="add-participants-table"
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={handleRowSelectionChange}
        renderControls={(table, { onSortClick } = {}) => {
          const totalMatchingCount = stagedResult?.totalCount ?? 0;

          return (
            <TableControls
              pagination={createHeaderPagination(stagedResult, {
                loading: participantsLoading,
                totalCount: stagedResult?.totalCount
              })}
              onSortClick={onSortClick}
              bulkActions={[
                {
                  label: "Remove Participant",
                  icon: "do_not_disturb_on" as const,
                  variant: "outlined" as const,
                  onClick: () => setRemoveParticipationModalOpen(true),
                  disabled: rowSelectionCount === 0
                },
                {
                  label: "Edit Participation",
                  variant: "outlined" as const,
                  onClick: () => setEditParticipationModalOpen(true),
                  disabled: rowSelectionCount === 0
                }
              ]}
              {...bulkSelectionProps}
              onDeselectAll={deselectAll}
              totalRowCount={rowSelectionCount}
              onToggleAllRows={(checked) => table.toggleAllRowsSelected(checked)}
              allRowsSelected={table.getIsAllRowsSelected()}
              someRowsSelected={table.getIsSomeRowsSelected()}
              onSelectAllAcrossPages={handleSelectAllAcrossPages}
              selectAllAcrossPagesLoading={fetchingAllIds}
              totalMatchingCount={totalMatchingCount}
            >
              <SearchInput
                placeholder="Search Participants"
                query={searchValue}
                onClear={() => {
                  clearSearch();
                  resetPagination();
                }}
                onChange={(e) => {
                  setSearchValue(e);
                  resetPagination();
                }}
              />
              <div data-name="validation">
                <Select
                  label="Validation"
                  options={VALIDATION_OPTIONS}
                  value={validationFilter}
                  onSelect={setValidationFilter}
                />
              </div>
            </TableControls>
          );
        }}
        hasActiveFilters={!!searchValue || validationFilter !== "all"}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Activity Participants"
            caption="Add participant individually or in bulk for this activity."
            iconName="person_add"
            action={{
              children: "Add Participants",
              icon: "add",
              onClick: () => setAddParticipantsModalOpen(true)
            }}
          />
        )}
        renderFooter={() => (
          <TableFooter
            pagination={
              !isSelectedOnly
                ? createFooterPagination(stagedResult, {
                    loading: participantsLoading,
                    totalCount: stagedResult?.totalCount
                  })
                : undefined
            }
            {...bulkSelectionProps}
            totalRowCount={stagedResult?.totalCount}
          />
        )}
      />

      {addParticipantsModalOpen && activityId && (
        <AddParticipantsModal
          onDismiss={() => setAddParticipantsModalOpen(false)}
          activityId={activityId}
          creditRates={activity?.activityType?.config?.creditRates ?? []}
          activityDuration={activity?.duration ?? null}
          allowParticipationStarting={activity?.allowParticipationStarting}
          allowParticipationThrough={activity?.allowParticipationThrough}
          onSuccess={(processingId) => {
            if (processingId) {
              setStagedImportId(processingId);
            }
            refetchParticipants();
            deselectAll();
          }}
          stagedImportId={stagedImportId}
        />
      )}

      {editParticipationModalOpen && (
        <EditParticipationModal
          onDismiss={() => setEditParticipationModalOpen(false)}
          selectedParticipants={selectedParticipants}
          selectedParticipantIds={Object.keys(rowSelection)}
          creditRates={activity?.activityType?.config?.creditRates ?? []}
          allowParticipationStarting={activity?.allowParticipationStarting}
          allowParticipationThrough={activity?.allowParticipationThrough}
          onSuccess={() => refetchParticipants()}
        />
      )}

      {removeParticipationModalOpen && (
        <RemoveParticipationModal
          onDismiss={() => setRemoveParticipationModalOpen(false)}
          selectedParticipantIds={Object.keys(rowSelection)}
          onSuccess={() => {
            refetchParticipants();
            deselectAll();
          }}
        />
      )}

      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(null);
        }}
        pgaId={selectedPgaId}
      />

      {cancelConfirmModalOpen && (
        <CancelSessionModal onDismiss={() => setCancelConfirmModalOpen(false)} onEraseAndLeave={handleEraseAndLeave} />
      )}

      <FinalizeParticipantsModal
        open={finalizeParticipantsModalOpen}
        onClose={() => setFinalizeParticipantsModalOpen(false)}
        onSuccess={() => setStagedImportId(null)}
        totalParticipants={newParticipantsCount}
        processingId={stagedImportId}
      />
    </FocusedPageLayout>
  );
}
